import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '@env/environment';
import { SecureHttp } from '@app/security/secure-http';
import { Language, deserializeLanguage } from '@app/core/models/language.model';
import {
  ClientLanguageConfig,
  ClientLanguageTexts,
  deserializeClientLanguageConfig,
} from '@app/core/models/client-language-config';
import {
  SmsCustomKeywordEntry,
  SmsKeywordType,
} from '@app/core/models/sms-custom-keyword';

/**
 * rn-portal's multi-language STOP/HELP config API,
 * `/client/:clientId/sms-language-config` (DIS-1660). Every route except
 * `/enabled` needs the `multi-language-stop-help` flag on for the client (404
 * otherwise) and the `sms_language:config:view` or `:edit` scope.
 */

// Type for SmsLanguageConfigService mocks — follows the ClientServiceT pattern (client.service.ts).
export interface SmsLanguageConfigServiceT {
  isEnabled?(clientId: string): Observable<boolean>;
  getLanguageLibrary?(clientId: string): Observable<Language[]>;
  getClientLanguages?(clientId: string): Observable<ClientLanguageConfig[]>;
  saveClientLanguages?(
    clientId: string,
    languages: ClientLanguageTexts[],
  ): Observable<ClientLanguageConfig[]>;
  removeLanguage?(
    clientId: string,
    languageCode: string,
  ): Observable<ClientLanguageConfig[]>;
  addCustomKeyword?(
    clientId: string,
    languageCode: string,
    type: SmsKeywordType,
    keyword: string,
  ): Observable<SmsCustomKeywordEntry>;
  removeCustomKeyword?(
    clientId: string,
    languageCode: string,
    keywordId: string,
  ): Observable<ClientLanguageConfig[]>;
  getProductGroupHelpReplies?(
    clientId: string,
    productGroupId: string,
  ): Observable<{ [languageCode: string]: string }>;
  saveProductGroupHelpReplies?(
    clientId: string,
    productGroupId: string,
    replies: { [languageCode: string]: string },
  ): Observable<unknown>;
  getReservedKeywords?(clientId: string): Observable<string[]>;
}

@Injectable()
export class SmsLanguageConfigService {
  constructor(private http: SecureHttp) {}

  private clientBase(clientId: string): string {
    return `${environment.portalURLBase}/client/${clientId}/sms-language-config`;
  }

  private deserializeClientLanguages(
    response: object[],
  ): ClientLanguageConfig[] {
    return (response || []).map(deserializeClientLanguageConfig);
  }

  /** The multi-language-stop-help flag, evaluated per client. No scope needed. */
  isEnabled(clientId: string): Observable<boolean> {
    return this.http
      .get(`${this.clientBase(clientId)}/enabled`, undefined, true)
      .pipe(
        map((response: { enabled: boolean }) => response?.enabled === true),
      );
  }

  /** The language library, with each language's system keywords. */
  getLanguageLibrary(clientId: string): Observable<Language[]> {
    const url = `${this.clientBase(clientId)}/languages`;
    return this.http
      .get(url)
      .pipe(
        map((response: object[]) => (response || []).map(deserializeLanguage)),
      );
  }

  /** This client's languages, English first, with server-computed status. */
  getClientLanguages(clientId: string): Observable<ClientLanguageConfig[]> {
    const url = `${this.clientBase(clientId)}/client-languages`;
    return this.http
      .get(url)
      .pipe(
        map((response: object[]) => this.deserializeClientLanguages(response)),
      );
  }

  /**
   * Adds languages (code only) or saves their STOP/HELP text. An upsert:
   * languages and fields left out are unchanged. Returns every language.
   */
  saveClientLanguages(
    clientId: string,
    languages: ClientLanguageTexts[],
  ): Observable<ClientLanguageConfig[]> {
    const url = `${this.clientBase(clientId)}/client-languages`;
    return this.http
      .put(url, { languages })
      .pipe(
        map((response: object[]) => this.deserializeClientLanguages(response)),
      );
  }

  /** Also removes the language's custom keywords and product-group help text. */
  removeLanguage(
    clientId: string,
    languageCode: string,
  ): Observable<ClientLanguageConfig[]> {
    const url = `${this.clientBase(clientId)}/client-languages/${languageCode}`;
    return this.http
      .delete(url)
      .pipe(
        map((response: object[]) => this.deserializeClientLanguages(response)),
      );
  }

  /**
   * POST a new custom keyword. A 409's `error.reason` says which language or
   * source (system, legacy, T2E, reserved) already uses the keyword; show it
   * inline rather than swallowing it.
   */
  addCustomKeyword(
    clientId: string,
    languageCode: string,
    type: SmsKeywordType,
    keyword: string,
  ): Observable<SmsCustomKeywordEntry> {
    const url = `${this.clientBase(
      clientId,
    )}/client-languages/${languageCode}/keywords`;
    return this.http
      .post(url, { type, keyword })
      .pipe(map((response) => SmsCustomKeywordEntry.deserialize(response)));
  }

  removeCustomKeyword(
    clientId: string,
    languageCode: string,
    keywordId: string,
  ): Observable<ClientLanguageConfig[]> {
    const url = `${this.clientBase(
      clientId,
    )}/client-languages/${languageCode}/keywords/${keywordId}`;
    return this.http
      .delete(url)
      .pipe(
        map((response: object[]) => this.deserializeClientLanguages(response)),
      );
  }

  /** A product group's help text by language code, Active languages only. */
  getProductGroupHelpReplies(
    clientId: string,
    productGroupId: string,
  ): Observable<{ [languageCode: string]: string }> {
    const url = `${this.clientBase(
      clientId,
    )}/product-groups/${productGroupId}/help-replies`;
    return this.http.get(url).pipe(
      map((response: { languageCode: string; replyText: string }[]) => {
        const replies: { [languageCode: string]: string } = {};
        (response || []).forEach((r) => (replies[r.languageCode] = r.replyText));
        return replies;
      }),
    );
  }

  /**
   * Saves help text for the languages given and leaves the others alone.
   * Blank text deletes that language's reply.
   */
  saveProductGroupHelpReplies(
    clientId: string,
    productGroupId: string,
    replies: { [languageCode: string]: string },
  ): Observable<unknown> {
    const url = `${this.clientBase(
      clientId,
    )}/product-groups/${productGroupId}/help-replies`;
    return this.http.put(url, {
      replies: Object.entries(replies).map(([languageCode, replyText]) => ({
        languageCode,
        replyText,
      })),
    });
  }

  /**
   * Every keyword a T2E keyword can't be for this client: system STOP/HELP
   * keywords of every library language, the client's custom keywords, its
   * shortcode's old keywords and reserved words. Needs the T2E page's
   * `t2e:config:view` scope, not the sms_language ones.
   */
  getReservedKeywords(clientId: string): Observable<string[]> {
    const url = `${this.clientBase(clientId)}/reserved-keywords`;
    return this.http
      .get(url, undefined, true)
      .pipe(
        map((response: { keyword: string }[]) =>
          (response || []).map((r) => r.keyword),
        ),
      );
  }
}
